import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAuth } from '../context/AuthContext';
import { useRegistrationStatus } from '../context/RegistrationStatusContext';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { NEW_API } from '../config/api';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button from '@mui/material/Button';
import axios from 'axios';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Tooltip from '@mui/material/Tooltip';
import IosShareIcon from '@mui/icons-material/IosShare';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ReorderIcon from '@mui/icons-material/Reorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useAppTokens } from '../context/ThemeModeContext';
import { getUsernameFromUsersubCF } from '../utils/userProfileApi';

const BOOK_OPEN_LEVEL_LABELS: Record<string, string> = {
  private: '非公開',
  friends: '友達のみ',
  url_share: 'リンク共有',
  public: '公開',
};

// MySQL datetime文字列（"2026-01-07 00:37:03"等）をそのまま整形する。
// Date型でパースするとブラウザのローカルタイムゾーンとして解釈されズレるため、文字列操作で対応
const formatRegisteredAt = (value: string): string | null => {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return value;
  const [, y, mo, d, h, mi] = m;
  return `${y}年${mo}月${d}日 ${h}:${mi}`;
};

interface Shelf {
  HondanaName: string;
  GlobalHondanaID: string;
  ColorCode: string;
  ShelfOrder: number;
  OpenLevel: string;
}

// 「本棚未分類」（rl_global_shelf_idがnullの本）の疑似棚を表す専用キー。
// 実際の本のGlobalHondanaIDは''（空文字）になるため、フィルタ側で相互変換する
const UNASSIGNED_SHELF_KEY = 'unassigned';

// 本棚の「カスタム並び順の最終更新時刻(ms)」。GET /shelves の一覧レスポンスから
// shelf_id -> custom_order_updated_at_ms の対応を作る（books ステートには乗らない
// 本棚メタ情報のため、shelves ステートとは別に保持する）
type ShelfCustomOrderMeta = Record<string, number | null>;

interface Book {
  global_book: {
    GlobalBookID: string;
    rl_book_id: number;
    GlobalHondanaID: string;
    BookOpenLevel: string;
    RegisteredDate: string;
    RemovedDate: string | null;
  };
  book_details: {
    MainTitle: string;
    ISBN: string;
    Responsibility: string;
    PublishingDate: string;
  };
  global_hondana: {
    GlobalHondanaID: string;
    HondanaName: string;
    ColorCode: string;
    OpenLevel: string;
    UserSub: string;
    ShelfOrder: number;
    Description: string | null;
  };
}

const UsersBookshelf: React.FC = () => {
  const TOKENS = useAppTokens();
  const [books, setBooks] = useState<Book[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [selectedBookForModal, setSelectedBookForModal] = useState<Book | null>(null);
  const [selectedShelf, setSelectedShelf] = useState<string>('all');
  const [sortCriteria, setSortCriteria] = useState<string>('newest');
  const [filterText, setFilterText] = useState<string>('');
  const { idToken, isLoggedIn, userSub } = useAuth();
  const { hasPending, pendingCounts, completionEventId, lastCompleted } = useRegistrationStatus();
  const [showRegistrationCompletedToast, setShowRegistrationCompletedToast] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // 本棚内カスタム並び順
  const [shelfCustomOrderMeta, setShelfCustomOrderMeta] = useState<ShelfCustomOrderMeta>({});
  const [customOrderCache, setCustomOrderCache] = useState<Record<string, string[]>>({});
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [reorderWorkingBooks, setReorderWorkingBooks] = useState<Book[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const reorderListRef = useRef<HTMLDivElement | null>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  const [totalBooks, setTotalBooks] = useState<number>(0);
  // GET /books?minimal=true の取得完了時点でtrueになる（2026-08-25〜）。この一括取得は
  // 書誌エンリッチを伴わない軽いDB問い合わせなので、旧実装（カーソルページングの全ページ
  // 完了待ち）と違い、タイトル等の表示を待たずに正確な冊数が早期に確定する
  const [booksFullyLoaded, setBooksFullyLoaded] = useState<boolean>(false);
  const [myUsername, setMyUsername] = useState<string | null>(null);

  // 公開ビュー（View As）用に自分のusernameを取得
  useEffect(() => {
    if (!userSub) return;
    getUsernameFromUsersubCF(userSub).then(setMyUsername).catch(() => setMyUsername(null));
  }, [userSub]);

  // 並び替え条件（ソート種別）をユーザーごとにlocalStorageへ保存し、次回ログイン時に復元する
  // （モバイル版 book_shelf_screen.dart の SharedPreferences 'sortOrder_$currentUserSub' と同じ考え方）。
  // sortCriteriaHydratedFor には「どのuserSub分を復元済みか」を持たせる。これにより
  // ①復元前の初期値('newest')で保存値を上書きしてしまう事故と、
  // ②Web版マルチアカウント切替時に前のユーザーの並び替え条件を引き継いでしまう事故の両方を防ぐ
  const [sortCriteriaHydratedFor, setSortCriteriaHydratedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!userSub || sortCriteriaHydratedFor === userSub) return;
    const saved = window.localStorage.getItem(`sortCriteria_${userSub}`);
    setSortCriteria(saved || 'newest');
    setSortCriteriaHydratedFor(userSub);
  }, [userSub, sortCriteriaHydratedFor]);

  useEffect(() => {
    if (!userSub || sortCriteriaHydratedFor !== userSub) return;
    window.localStorage.setItem(`sortCriteria_${userSub}`, sortCriteria);
  }, [userSub, sortCriteriaHydratedFor, sortCriteria]);

  const handleViewAsPublic = useCallback(() => {
    if (!myUsername) return;
    window.open(`/bookshelf/${myUsername}`, '_blank', 'noopener,noreferrer');
  }, [myUsername]);

  // 未ログインならトップへ自動遷移
  useEffect(() => {
    if (isLoggedIn) return;

    setIsLoading(false);
    setError(null);

    const t = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 1200);

    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  // 本データ取得（新 API: GET /shelves + GET /books のカーソルページング）
  const fetchUserBookshelfData = useCallback(async () => {
    if (!idToken) return;

    setIsLoading(true);
    setError(null);
    setBooksFullyLoaded(false);

    try {
      const headers = { Authorization: `Bearer ${idToken}` };

      // 本棚メタ情報（名前・色・並び順・冊数）
      const shelvesRes = await axios.get(NEW_API.SHELVES, { headers });
      const shelfItems: any[] = shelvesRes.data?.data?.items ?? [];
      const shelfMap = new Map<string, any>();
      shelfItems.forEach((s) => shelfMap.set(s.rl_global_shelf_id, s));

      const orderMeta: ShelfCustomOrderMeta = {};
      shelfItems.forEach((s) => {
        orderMeta[s.rl_global_shelf_id] = s.custom_order_updated_at_ms ?? null;
      });
      setShelfCustomOrderMeta(orderMeta);

      // 本棚割り当てを一括取得（GET /books?minimal=true）。
      // 外部API呼び出し（書誌情報のエンリッチ）が絡まないDBだけの問い合わせなので、
      // カーソルページング無しで全冊を1回のリクエストで取れる（旧: 100冊ずつ逐次取得）。
      // これにより「全X冊」の冊数もこの時点で正確に確定する（GET /shelvesのbook_count
      // 合計は本棚未分類の本を含まず過小だった問題は、そもそも見積もりを使わなくなり解消）
      //
      // バックエンド側は1回のレスポンスサイズを安全な範囲（Lambda同期レスポンス上限6MB）に
      // 保つため、内部的にはcursorページング（1回最大10000件）になっている。数万〜10万冊
      // 規模の蔵書でも取りこぼしが起きないよう、next_cursorが無くなるまでループする
      // （このループ自体は外部API呼び出しを伴わない軽いDB問い合わせの繰り返しなので高速）
      const rawItems: any[] = [];
      let minimalCursor: string | null = null;
      do {
        const minimalParams: Record<string, string> = { minimal: 'true' };
        if (minimalCursor) minimalParams.cursor = minimalCursor;
        const minimalRes = await axios.get(NEW_API.BOOKS, { headers, params: minimalParams });
        const data = minimalRes.data?.data ?? {};
        rawItems.push(...(data.items ?? []));
        minimalCursor = data.next_cursor ?? null;
      } while (minimalCursor);

      const allBooks: Book[] = rawItems.map((item) => {
        const shelf = item.rl_global_shelf_id ? shelfMap.get(item.rl_global_shelf_id) : undefined;
        return {
          global_book: {
            GlobalBookID: item.global_book_id,
            rl_book_id: item.rl_book_id,
            GlobalHondanaID: item.rl_global_shelf_id ?? '',
            BookOpenLevel: item.global_book_open_level ?? 'private',
            RegisteredDate: item.global_book_registered_at ?? '',
            RemovedDate: null, // GET /books は除籍済みを返さない
          },
          book_details: {
            // タイトル等はこの時点では未取得。後続のエンリッチで埋まる
            MainTitle: '',
            ISBN: '',
            Responsibility: '',
            PublishingDate: '',
          },
          global_hondana: {
            GlobalHondanaID: item.rl_global_shelf_id ?? '',
            HondanaName: shelf?.global_shelf_name ?? '',
            ColorCode: shelf?.global_shelf_color_code ?? '#FFFFFF',
            OpenLevel: shelf?.global_shelf_open_level ?? 'private',
            UserSub: '',
            ShelfOrder:
              typeof shelf?.global_shelf_order === 'number'
                ? shelf.global_shelf_order
                : 999,
            Description: shelf?.global_shelf_description ?? null,
          },
        };
      });

      // 本棚の骨組み（何冊あるか・どの本棚に何が入っているか）はこの時点で全て確定。
      // タイトル等を待たず即座に描画し、正確な冊数も先に確定させる
      setBooks([...allBooks]);
      setTotalBooks(allBooks.length);
      setBooksFullyLoaded(true);
      setIsLoading(false);
      setIsLoadingMore(allBooks.length > 0);

      // 書誌情報のエンリッチ（重い・外部API呼び出しが絡む部分）をチャンクに分割し、
      // 複数レーンで並行取得する。同じ rl_book_id を複数冊所有している場合に備え、
      // rl_book_id -> それを持つ本(複数の場合あり)のindex一覧を先に作っておく
      const indexByRlBookId = new Map<number, number[]>();
      allBooks.forEach((b, i) => {
        const id = b.global_book.rl_book_id;
        const list = indexByRlBookId.get(id);
        if (list) list.push(i);
        else indexByRlBookId.set(id, [i]);
      });
      const uniqueRlBookIds = Array.from(indexByRlBookId.keys());

      const CHUNK_SIZE = 100; // バックエンド側の enrich_display 上限(200)に対して余裕を持たせる
      const LANES = 4;
      const chunks: number[][] = [];
      for (let i = 0; i < uniqueRlBookIds.length; i += CHUNK_SIZE) {
        chunks.push(uniqueRlBookIds.slice(i, i + CHUNK_SIZE));
      }

      let nextChunkIndex = 0;
      const runLane = async () => {
        while (nextChunkIndex < chunks.length) {
          const chunk = chunks[nextChunkIndex++];
          try {
            const res = await axios.post(
              NEW_API.BOOKS,
              { operation_type: 'enrich_display', rl_book_ids: chunk },
              { headers }
            );
            const displayMap: Record<string, any> = res.data?.data?.items ?? {};
            Object.entries(displayMap).forEach(([rlBookIdStr, disp]: [string, any]) => {
              const indices = indexByRlBookId.get(Number(rlBookIdStr));
              if (!indices) return;
              indices.forEach((idx) => {
                allBooks[idx] = {
                  ...allBooks[idx],
                  book_details: {
                    MainTitle: disp?.title ?? '(タイトル情報なし)',
                    ISBN: disp?.isbn13 ?? '',
                    Responsibility: disp?.author ?? '',
                    PublishingDate: disp?.pub_year != null ? String(disp.pub_year) : '',
                  },
                };
              });
            });
            setBooks([...allBooks]); // チャンクが返ってくるたびに逐次描画
          } catch (chunkErr) {
            // 1チャンクの失敗で全体の読み込みは止めない（該当分だけタイトル情報が空のまま残る）
            console.error('enrich_display chunk failed', chunkErr);
          }
        }
      };

      if (chunks.length > 0) {
        await Promise.all(
          Array.from({ length: Math.min(LANES, chunks.length) }, () => runLane())
        );
      }
    } catch (err) {
      setError('An error occurred while fetching the bookshelf data');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [idToken]);

  // 棚リスト生成
  useEffect(() => {
    const shelvesMap = new Map<string, Shelf>();

    shelvesMap.set('all', {
      HondanaName: 'すべての本',
      GlobalHondanaID: 'all',
      ColorCode: '#808080',
      ShelfOrder: -1,
      OpenLevel: ''
    });

    let hasUnassigned = false;

    books.forEach(book => {
      if (book.global_book.RemovedDate === null) {
        const { GlobalHondanaID, HondanaName, ColorCode, ShelfOrder, OpenLevel } = book.global_hondana;
        if (GlobalHondanaID && !shelvesMap.has(GlobalHondanaID)) {
          shelvesMap.set(GlobalHondanaID, {
            GlobalHondanaID,
            HondanaName,
            ColorCode: ColorCode || '#FFFFFF',
            ShelfOrder: typeof ShelfOrder === 'number' ? ShelfOrder : 999,
            OpenLevel: OpenLevel || 'private'
          });
        } else if (!GlobalHondanaID) {
          hasUnassigned = true;
        }
      }
    });

    // 本棚未分類（rl_global_shelf_idがnullの本）はモバイル版と同様、専用の
    // 疑似棚として一覧の末尾に表示する（未分類の本が実際に存在する場合のみ）
    if (hasUnassigned) {
      shelvesMap.set(UNASSIGNED_SHELF_KEY, {
        HondanaName: '本棚未分類',
        GlobalHondanaID: UNASSIGNED_SHELF_KEY,
        ColorCode: '#808080',
        ShelfOrder: 9999,
        OpenLevel: ''
      });
    }

    const newShelves = Array.from(shelvesMap.values()).sort((a, b) => {
      if (a.GlobalHondanaID === 'all') return -1;
      if (b.GlobalHondanaID === 'all') return 1;
      return a.ShelfOrder - b.ShelfOrder;
    });

    setShelves(newShelves);
  }, [books]);

  // ログインしている時だけデータ取得
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchUserBookshelfData();
  }, [isLoggedIn, idToken, fetchUserBookshelfData]);

  // バルク登録の完了を検知したら、手動リロードを待たずに本棚を再取得する
  const prevCompletionEventIdRef = useRef(completionEventId);
  useEffect(() => {
    if (completionEventId !== prevCompletionEventIdRef.current) {
      prevCompletionEventIdRef.current = completionEventId;
      if (isLoggedIn) fetchUserBookshelfData();
      setShowRegistrationCompletedToast(true);
      const timer = setTimeout(() => setShowRegistrationCompletedToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [completionEventId, isLoggedIn, fetchUserBookshelfData]);

  // 通常の本（除籍済みでないもの）
  const activeBooks = useMemo(() => {
    return books.filter(book => book.global_book.RemovedDate === null);
  }, [books]);

  // 書誌情報（タイトル等）のエンリッチが完了済みの冊数。冊数自体は取得済み割り当てから
  // 即座に確定しているため、ここではチャンク並列取得の進捗表示にのみ使う
  const enrichedCount = useMemo(() => {
    return books.filter((b) => b.book_details.MainTitle !== '').length;
  }, [books]);

  // 棚ごとの冊数
  const shelfBookCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};
    activeBooks.forEach(book => {
      const shelfId = book.global_book.GlobalHondanaID || UNASSIGNED_SHELF_KEY;
      counts[shelfId] = (counts[shelfId] || 0) + 1;
    });
    counts['all'] = activeBooks.length;
    return counts;
  }, [activeBooks]);

  const handleBookClick = (book: Book) => {
    setSelectedBookForModal(book);
  };

  const handleViewBookDetail = (book: Book) => {
    setSelectedBookForModal(null);
    window.open(
      `/book/${book.global_book.rl_book_id}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleShareBook = async (book: Book) => {
    const url = `https://readlinker.com/globalbook/${book.global_book.GlobalBookID}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: book.book_details.MainTitle, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('個別資料URLをコピーしました');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // 除籍（モバイル版mybook_detail_screen.dartと同じ確認文言・同じbulk_withdraw APIを使用。
  // 非同期のキュー処理だが、モーダル上は即座に一覧から消して楽観的に反映する）
  const handleWithdrawBook = async (book: Book) => {
    if (!idToken) return;
    const confirmed = window.confirm(
      `「${book.book_details.MainTitle}」を除籍しますか？本棚一覧では表示されなくなります。`
    );
    if (!confirmed) return;

    try {
      const headers = { Authorization: `Bearer ${idToken}` };
      await axios.post(
        NEW_API.BOOKS,
        { operation_type: 'bulk_withdraw', items: [{ global_book_id: book.global_book.GlobalBookID }] },
        { headers }
      );
      setBooks((prev) => prev.filter((b) => b.global_book.GlobalBookID !== book.global_book.GlobalBookID));
      setSelectedBookForModal(null);
    } catch (error) {
      console.error('Error withdrawing book:', error);
      alert('除籍に失敗しました');
    }
  };

  const filterBooks = (book: Book) => {
    const searchText = filterText.toLowerCase();
    const { MainTitle, Responsibility } = book.book_details;
    return (
      MainTitle.toLowerCase().includes(searchText) ||
      Responsibility.toLowerCase().includes(searchText)
    );
  };

  const sortBooks = (arr: Book[]) => {
    switch (sortCriteria) {
      case 'newest':
        return arr
          .slice()
          .sort(
            (a, b) =>
              new Date(b.global_book.RegisteredDate).getTime() -
              new Date(a.global_book.RegisteredDate).getTime()
          );
      case 'oldest':
        return arr
          .slice()
          .sort(
            (a, b) =>
              new Date(a.global_book.RegisteredDate).getTime() -
              new Date(b.global_book.RegisteredDate).getTime()
          );
      case 'pub-newest':
        return arr
          .slice()
          .sort(
            (a, b) =>
              new Date(b.book_details.PublishingDate).getTime() -
              new Date(a.book_details.PublishingDate).getTime()
          );
      case 'pub-oldest':
        return arr
          .slice()
          .sort(
            (a, b) =>
              new Date(a.book_details.PublishingDate).getTime() -
              new Date(b.book_details.PublishingDate).getTime()
          );
      case 'custom': {
        const orderList = customOrderCache[selectedShelf] ?? [];
        const indexMap = new Map(orderList.map((id, i) => [id, i]));
        return arr.slice().sort((a, b) => {
          const ia = indexMap.get(a.global_book.GlobalBookID);
          const ib = indexMap.get(b.global_book.GlobalBookID);
          if (ia !== undefined && ib !== undefined) return ia - ib;
          // 並び順未設定の本（新規追加分など）は末尾へ、登録日が新しい順で並べる
          if (ia !== undefined) return -1;
          if (ib !== undefined) return 1;
          return (
            new Date(b.global_book.RegisteredDate).getTime() -
            new Date(a.global_book.RegisteredDate).getTime()
          );
        });
      }
      default:
        return arr;
    }
  };

  const filteredBooks = useMemo(() => {
    return books
      .filter(book => {
        if (selectedShelf === 'all') return true;
        if (selectedShelf === UNASSIGNED_SHELF_KEY) return !book.global_book.GlobalHondanaID;
        return book.global_book.GlobalHondanaID === selectedShelf;
      })
      .filter(filterBooks);
  }, [books, selectedShelf, filterText]);

  const sortedBooks = useMemo(() => {
    return sortBooks(filteredBooks);
  }, [filteredBooks, sortCriteria, customOrderCache, selectedShelf]);

  // 蔵書グリッドの仮想化（2026-08-25追加）。sortedBooks.mapで全件を一括DOM化すると
  // 数万〜10万冊規模のユーザーでブラウザが重くなる（実データでは最大でも数千冊だが、
  // 「セミプロ級の使い手が10万冊まで使えるアプリ」を目指すため、件数に依存せず軽い設計にする）。
  // MUIのGrid columnsと同じブレークポイント(xs:2 / sm:4 / md以上:6)を手動で再現し、
  // 行単位で仮想スクロールする（列内はCSS Gridでレイアウト、行の縦位置だけtransformで制御）
  const virtualScrollRef = useRef<HTMLDivElement | null>(null);
  const isSmUp = useMediaQuery('(min-width:600px)');
  const isMdUp = useMediaQuery('(min-width:900px)');
  const columnCount = isMdUp ? 6 : isSmUp ? 4 : 2;
  const BOOK_ROW_HEIGHT = 284; // カード230px + タイトル/著者2行(約38px) + 行間ギャップ16px
  const bookRowCount = Math.ceil(sortedBooks.length / columnCount);
  const rowVirtualizer = useVirtualizer({
    count: bookRowCount,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => BOOK_ROW_HEIGHT,
    overscan: 3,
  });

  const getSelectedShelfColor = () => {
    const selected = shelves.find(shelf => shelf.GlobalHondanaID === selectedShelf);
    return selected ? selected.ColorCode : '#808080';
  };

  const selectedShelfName = useMemo(() => {
    const s = shelves.find(x => x.GlobalHondanaID === selectedShelf);
    return s ? s.HondanaName : '';
  }, [shelves, selectedShelf]);

  // 棚の公開/非公開アイコン。デスクトップのサイドバーとモバイルの棚選択メニュー、
  // 両方の表示で共通利用する（「すべての本」「未分類」等の疑似棚はOpenLevelが
  // 空文字のため、どちらのアイコンも出さない）
  const renderShelfVisibilityIcon = (shelf: Shelf) => {
    if (!shelf.OpenLevel) return null;
    return shelf.OpenLevel === 'public' ? (
      <PublicIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="公開" />
    ) : (
      <LockIcon sx={{ fontSize: 13, color: TOKENS.textSub }} titleAccess="非公開" />
    );
  };

  // モバイル用「棚選択」メニューの開閉状態
  const [shelfMenuAnchorEl, setShelfMenuAnchorEl] = useState<null | HTMLElement>(null);

  // 本棚内カスタム並び順を取得してキャッシュに乗せる。取得結果（global_book_idの配列）を返す
  const fetchCustomOrder = useCallback(
    async (shelfId: string): Promise<string[]> => {
      if (!idToken) return [];
      try {
        const headers = { Authorization: `Bearer ${idToken}` };
        const res = await axios.get(NEW_API.SHELVES, {
          headers,
          params: { shelf_id: shelfId, book_order: 'true' },
        });
        const items: any[] = res.data?.data?.items ?? [];
        const orderedIds = items.map((i) => i.global_book_id as string);
        setCustomOrderCache((prev) => ({ ...prev, [shelfId]: orderedIds }));
        return orderedIds;
      } catch (err) {
        console.error('カスタム並び順の取得に失敗:', err);
        return [];
      }
    },
    [idToken]
  );

  // カスタム並び替えは実在の本棚にしか紐付けられない（「すべての本」「本棚未分類」は
  // バックエンド上のrl_global_shelf_idを持たない疑似棚のため対象外）
  const hasRealShelfSelected = selectedShelf !== 'all' && selectedShelf !== UNASSIGNED_SHELF_KEY;

  // 「カスタム」ソートを選んだ時点でまだキャッシュが無ければ取得する
  useEffect(() => {
    if (sortCriteria !== 'custom' || !hasRealShelfSelected) return;
    if (customOrderCache[selectedShelf]) return;
    fetchCustomOrder(selectedShelf);
  }, [sortCriteria, selectedShelf, hasRealShelfSelected, customOrderCache, fetchCustomOrder]);

  const openReorderDialog = useCallback(async () => {
    if (!hasRealShelfSelected) return;
    setReorderDialogOpen(true);
    setReorderLoading(true);
    const orderList = customOrderCache[selectedShelf] ?? (await fetchCustomOrder(selectedShelf));
    const shelfBooks = activeBooks.filter((b) => b.global_book.GlobalHondanaID === selectedShelf);
    const indexMap = new Map(orderList.map((id, i) => [id, i]));
    const sorted = [...shelfBooks].sort((a, b) => {
      const ia = indexMap.get(a.global_book.GlobalBookID);
      const ib = indexMap.get(b.global_book.GlobalBookID);
      if (ia !== undefined && ib !== undefined) return ia - ib;
      if (ia !== undefined) return -1;
      if (ib !== undefined) return 1;
      return (
        new Date(b.global_book.RegisteredDate).getTime() -
        new Date(a.global_book.RegisteredDate).getTime()
      );
    });
    setReorderWorkingBooks(sorted);
    setReorderLoading(false);
  }, [selectedShelf, hasRealShelfSelected, customOrderCache, fetchCustomOrder, activeBooks]);

  // ドラッグ中にリストの上端・下端付近にカーソルが来たら自動でスクロールする
  // （長い本棚だと、都度ドラッグ→手動スクロール→ドラッグの繰り返しになって使いづらいため）
  const stopReorderAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current !== null) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  // コンポーネントごと消える場合の後始末（ドラッグ中にページ遷移する等のレアケース）
  useEffect(() => stopReorderAutoScroll, [stopReorderAutoScroll]);

  const handleReorderDrop = (targetIndex: number) => {
    setReorderWorkingBooks((prev) => {
      if (draggedIndex === null || draggedIndex === targetIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDraggedIndex(null);
    stopReorderAutoScroll();
  };

  const handleReorderListDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = reorderListRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const edgeZone = 56; // この範囲内にカーソルが入ったら自動スクロール開始
    const maxSpeed = 18; // px/tick
    const distFromTop = e.clientY - rect.top;
    const distFromBottom = rect.bottom - e.clientY;

    let speed = 0;
    if (distFromTop >= 0 && distFromTop < edgeZone) {
      speed = -Math.ceil(maxSpeed * (1 - distFromTop / edgeZone));
    } else if (distFromBottom >= 0 && distFromBottom < edgeZone) {
      speed = Math.ceil(maxSpeed * (1 - distFromBottom / edgeZone));
    }

    stopReorderAutoScroll();
    if (speed !== 0) {
      autoScrollIntervalRef.current = window.setInterval(() => {
        container.scrollTop += speed;
      }, 16);
    }
  };

  // ドラッグ操作の代替として、本棚管理画面と同じ上下ボタンでも並び替えられるようにする
  const moveReorderItem = (index: number, direction: -1 | 1) => {
    setReorderWorkingBooks((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const handleSaveReorder = async () => {
    if (!idToken || !hasRealShelfSelected || reorderSaving) return;
    setReorderSaving(true);
    try {
      const headers = { Authorization: `Bearer ${idToken}` };
      const orderedIds = reorderWorkingBooks.map((b) => b.global_book.GlobalBookID);
      const res = await axios.post(
        NEW_API.SHELVES,
        {
          operation_type: 'reorder_shelf_books',
          shelf_id: selectedShelf,
          ordered_global_book_ids: orderedIds,
        },
        { headers }
      );
      const updatedAtMs: number | undefined = res.data?.data?.updated_at_ms;
      setCustomOrderCache((prev) => ({ ...prev, [selectedShelf]: orderedIds }));
      setShelfCustomOrderMeta((prev) => ({ ...prev, [selectedShelf]: updatedAtMs ?? Date.now() }));
      setSortCriteria('custom');
      setReorderDialogOpen(false);
    } catch (err) {
      console.error('並び順の保存に失敗:', err);
      alert('並び順の保存に失敗しました');
    } finally {
      setReorderSaving(false);
    }
  };

  // 未ログイン時の表示
  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: TOKENS.bg,
          color: TOKENS.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}
      >
        <Box
          sx={{
            width: 'min(640px, 100%)',
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glass,
            borderRadius: '16px',
            p: 2.5
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, letterSpacing: 0.2 }}>
            ログインすると本棚を利用できます
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 12, color: TOKENS.textSub }}>
            トップページへ移動します…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: TOKENS.bg,
          color: TOKENS.text,
          px: 2,
          pt: 10
        }}
      >
        <Box
          sx={{
            width: 'min(760px, 100%)',
            mx: 'auto',
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glass,
            borderRadius: '16px',
            p: 2.5
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            本棚を読み込んでいます...
          </Typography>
          <LinearProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: TOKENS.bg,
          color: TOKENS.text,
          px: 2,
          pt: 10
        }}
      >
        <Box
          sx={{
            width: 'min(760px, 100%)',
            mx: 'auto',
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glass,
            borderRadius: '16px',
            p: 2.5
          }}
        >
          <Typography sx={{ fontSize: 13, color: TOKENS.error }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx = {{mt:7}}>
    <Box
      sx={{
        minHeight: 'calc(100vh - 56px)',
        background: TOKENS.bg,
        color: TOKENS.text,
        overflow: 'hidden'
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          height: 'calc(100vh - 56px)',
          py: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 1.5 }}>本棚</Typography>

        {/* Top status + controls (glass panel) */}
        <Box
          sx={{
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.glass,
            borderRadius: '16px',
            p: 1.5,
            mb: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}
          >
            {/* モバイル専用「棚選択」ボタン。デスクトップの棚一覧サイドバーは
                display:noneで非表示にしているため、代わりにこのMenuから棚を選ぶ */}
            <Button
              onClick={(e) => setShelfMenuAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                borderRadius: '999px',
                px: 1.5,
                py: 0.75,
                minWidth: 0,
                background: TOKENS.glassStrong,
                border: `1px solid ${TOKENS.border}`,
                color: TOKENS.text,
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { background: TOKENS.glassStrong }
              }}
            >
              {selectedShelfName || 'すべての本'}
              {selectedShelf && shelfBookCounts[selectedShelf] != null
                ? ` (${shelfBookCounts[selectedShelf]})`
                : ''}
            </Button>
            <Menu
              anchorEl={shelfMenuAnchorEl}
              open={Boolean(shelfMenuAnchorEl)}
              onClose={() => setShelfMenuAnchorEl(null)}
              PaperProps={{ sx: { maxHeight: '70vh' } }}
            >
              {shelves.map((shelf) => {
                const isSelected = selectedShelf === shelf.GlobalHondanaID;
                const count = shelfBookCounts[shelf.GlobalHondanaID] || 0;
                return (
                  <MenuItem
                    key={shelf.GlobalHondanaID}
                    selected={isSelected}
                    onClick={() => {
                      setSelectedShelf(shelf.GlobalHondanaID);
                      setShelfMenuAnchorEl(null);
                    }}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13 }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, flexGrow: 1 }}
                    >
                      {shelf.HondanaName}
                    </Typography>
                    {renderShelfVisibilityIcon(shelf)}
                    <Typography component="span" sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                      ({count})
                    </Typography>
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Status pill */}
            <Box
              sx={{
                borderRadius: '999px',
                px: 1.5,
                py: 0.75,
                background: TOKENS.glassStrong,
                border: `1px solid ${TOKENS.border}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.2 }}>
                {booksFullyLoaded ? `全 ${totalBooks} 冊` : '読み込み中…'}
              </Typography>
              {selectedShelfName ? (
                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, display: { xs: 'none', md: 'inline' } }}>
                  / {selectedShelfName}
                </Typography>
              ) : null}
            </Box>

            {isLoadingMore ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={12} sx={{ color: TOKENS.textSub }} />
                <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>
                  書誌情報 {enrichedCount}/{totalBooks} 件取得済み
                </Typography>
              </Box>
            ) : null}

            {/* バルク登録（バーコードスキャン等）の進捗表示。他端末・他タブでの
                登録も、常時ポーリングしているここで検知できる */}
            {hasPending ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={12} sx={{ color: TOKENS.accent }} />
                <Typography sx={{ fontSize: 12, color: TOKENS.accent }}>
                  {pendingCounts.register}冊を本棚に登録中…
                </Typography>
              </Box>
            ) : showRegistrationCompletedToast ? (
              <Typography sx={{ fontSize: 12, color: TOKENS.accent }}>
                {(() => {
                  const register = lastCompleted?.results?.register;
                  const succeeded = register?.succeeded ?? 0;
                  const failed = register?.failed ?? 0;
                  if (succeeded === 0 && failed === 0) return null;
                  return failed > 0
                    ? `✓ ${succeeded}冊を追加しました（${failed}冊は失敗）`
                    : `✓ ${succeeded}冊を追加しました`;
                })()}
              </Typography>
            ) : null}

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
              onClick={handleViewAsPublic}
              disabled={!myUsername}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                color: TOKENS.text,
                borderColor: TOKENS.borderSoft,
                '&:hover': {
                  borderColor: TOKENS.accent,
                  background: TOKENS.glassStrong
                }
              }}
            >
              公開ビューを見る
            </Button>

            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  borderRadius: '12px'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: TOKENS.borderSoft
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: TOKENS.accent
                },
                '& .MuiSvgIcon-root': {
                  color: TOKENS.textSub
                },
                '& .MuiInputLabel-root': {
                  color: TOKENS.textSub
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: TOKENS.text
                }
              }}
            >
              <InputLabel>並び替え</InputLabel>
              <Select
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value as string)}
                label="並び替え"
              >
                <MenuItem value="newest">登録順(新しい順)</MenuItem>
                <MenuItem value="oldest">登録順(古い順)</MenuItem>
                <MenuItem value="pub-newest">出版日順(新しい順)</MenuItem>
                <MenuItem value="pub-oldest">出版日順(古い順)</MenuItem>
                <MenuItem value="custom" disabled={!hasRealShelfSelected}>
                  カスタム
                </MenuItem>
              </Select>
            </FormControl>

            {/* 「カスタム」選択中のみ有効。それ以外の時は visibility: hidden で幅だけ確保し、
                本棚選択やソート切り替えのたびに隣のボタン位置がガタつかないようにする */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReorderIcon sx={{ fontSize: 16 }} />}
              onClick={openReorderDialog}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                color: TOKENS.text,
                borderColor: TOKENS.borderSoft,
                visibility: sortCriteria === 'custom' && hasRealShelfSelected ? 'visible' : 'hidden',
                '&:hover': {
                  borderColor: TOKENS.accent,
                  background: TOKENS.glassStrong,
                },
              }}
            >
              並び替え
            </Button>

            <TextField
              label="フィルター"
              size="small"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              variant="outlined"
              sx={{
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  borderRadius: '12px'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: TOKENS.borderSoft
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: TOKENS.accent
                },
                '& .MuiInputLabel-root': {
                  color: TOKENS.textSub
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: TOKENS.text
                }
              }}
              InputProps={{
                endAdornment: filterText ? (
                  <IconButton
                    onClick={() => setFilterText('')}
                    size="small"
                    sx={{
                      color: TOKENS.textSub,
                      '&:hover': {
                        color: TOKENS.text,
                        background: 'rgba(148,163,184,0.10)'
                      }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
            />
          </Box>
        </Box>

        {/* Main split layout: internal scroll only */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexGrow: 1,
            minHeight: 0,
            overflow: 'hidden',
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          {/* Left panel (shelves)。狭い画面では棚数が多いと縦にどこまでも伸びて
              下の本グリッド（仮想スクロール）が画面外に押し出されてしまうため、
              モバイルではこのサイドバーごと非表示にし、代わりに上部コントロール行の
              「棚選択」ボタン→Menuに置き換える（下記参照） */}
          <Box
            sx={{
              width: { xs: '100%', md: 260 },
              flexShrink: 0,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.buttonBg,
              borderRadius: '16px',
              overflow: 'hidden',
              minHeight: { xs: 220, md: 0 },
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${TOKENS.border}`,
                background: TOKENS.glass
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.text, letterSpacing: 0.2 }}>
                棚
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 0.25 }}>
                選択中の棚だけを表示
              </Typography>
            </Box>

<Box
  sx={{
    px: 1.25,
    py: 1.0,
    borderBottom: `1px solid ${TOKENS.border}`,
    background: TOKENS.cardBg,
    display: 'flex',
    alignItems: 'center'
  }}
>
</Box>


            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
              <List sx={{ py: 0 }}>
                {shelves.map((shelf) => {
                  const isSelected = selectedShelf === shelf.GlobalHondanaID;
                  const count = shelfBookCounts[shelf.GlobalHondanaID] || 0;

                  return (
                    <ListItem key={shelf.GlobalHondanaID} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedShelf(shelf.GlobalHondanaID)}
                        sx={{
                          px: 1.5,
                          py: 1.0,
                          alignItems: 'flex-start',
                          borderLeft: isSelected ? `3px solid ${getSelectedShelfColor()}` : '3px solid transparent',
                          '&.Mui-selected': {
                            background: 'rgba(148,163,184,0.10)'
                          },
                          '&.Mui-selected:hover': {
                            background: 'rgba(148,163,184,0.14)'
                          },
                          '&:hover': {
                            background: 'rgba(148,163,184,0.08)'
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 12.5,
                                  lineHeight: '1.25em',
                                  color: TOKENS.text,
                                  fontWeight: isSelected ? 700 : 500
                                }}
                              >
                                {shelf.HondanaName}
                              </Typography>
                              {renderShelfVisibilityIcon(shelf)}
                              <Typography component="span" sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                                ({count})
                              </Typography>
                            </Box>
                          }
                          sx={{
                            m: 0,
                            '& .MuiListItemText-primary': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>

          {/* Right panel (books) */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.buttonBg,
              borderRadius: '16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${TOKENS.border}`,
                background: TOKENS.glass,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box
                sx={{
                  borderRadius: '999px',
                  px: 1.25,
                  py: 0.6,
                  background: TOKENS.glass,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '999px',
                    background: getSelectedShelfColor(),
                    opacity: 0.85,
                    boxShadow: '0 0 0 2px rgba(2,6,23,0.35)'
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {selectedShelfName || '表示'}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                {sortedBooks.length} 件
              </Typography>
            </Box>

            <Box ref={virtualScrollRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 1.5 }}>
              <Box sx={{ position: 'relative', width: '100%', height: rowVirtualizer.getTotalSize() }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const startIdx = virtualRow.index * columnCount;
                  const rowBooks = sortedBooks.slice(startIdx, startIdx + columnCount);
                  return (
                    <Box
                      key={String(virtualRow.key)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                        gap: 2,
                      }}
                    >
                      {rowBooks.map((book) => {
                        const key = book.global_book.GlobalBookID;
                        const title = book.book_details.MainTitle;
                        const author = book.book_details.Responsibility;
                        const imageUrl = generateCoverUrlSmart(book.global_book.rl_book_id, book.book_details.MainTitle, book.book_details.ISBN);

                        return (
                          <Box key={key} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
                            <Card
                              onClick={() => handleBookClick(book)}
                              sx={{
                                height: 230,
                                display: 'flex',
                                flexDirection: 'column',
                                background: TOKENS.buttonBg,
                                border: `1px solid ${TOKENS.borderSoft}`,
                                borderRadius: '14px',
                                overflow: 'hidden',
                                transition: '0.18s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                  borderColor: TOKENS.accent,
                                  transform: 'translateY(-1px)'
                                }
                              }}
                            >

                              <Box
                                sx={{
                                  flexGrow: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  p: 1.25,
                                  background: TOKENS.cardBg
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  sx={{
                                    height: 170,
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    background: TOKENS.cardBg
                                  }}
                                  image={imageUrl}
                                  alt={title}
                                />
                              </Box>
                            </Card>

                            <Box sx={{ mt: 1, textAlign: 'left', px: 0.25 }}>
                              <Typography
                                sx={{
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  color: TOKENS.text
                                }}
                                noWrap
                                title={title}
                              >
                                {title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11.5,
                                  color: TOKENS.textSub
                                }}
                                noWrap
                                title={author}
                              >
                                {author}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>

    <Dialog
      open={Boolean(selectedBookForModal)}
      onClose={() => setSelectedBookForModal(null)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: TOKENS.bg,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 3,
        },
      }}
    >
      {selectedBookForModal && (
        <>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.textSub, mb: 2 }}>
              本棚の本の詳細
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Card
                sx={{
                  width: 90,
                  flexShrink: 0,
                  boxShadow: 'none',
                  background: TOKENS.cardBg,
                  border: `1px solid ${TOKENS.border}`,
                }}
              >
                <CardMedia
                  component="img"
                  image={generateCoverUrlSmart(selectedBookForModal.global_book.rl_book_id, selectedBookForModal.book_details.MainTitle, selectedBookForModal.book_details.ISBN)}
                  alt={selectedBookForModal.book_details.MainTitle}
                  sx={{ objectFit: 'contain', height: 120, width: '100%', padding: '4px' }}
                />
              </Card>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: TOKENS.text, lineHeight: 1.3 }}>
                  {selectedBookForModal.book_details.MainTitle}
                </Typography>
                {selectedBookForModal.book_details.Responsibility && (
                  <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.5 }}>
                    {selectedBookForModal.book_details.Responsibility}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${TOKENS.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '999px',
                      background: selectedBookForModal.global_hondana.ColorCode || '#FFFFFF',
                      border: `1px solid ${TOKENS.borderSoft}`,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                    {selectedBookForModal.global_hondana.HondanaName || '（未分類）'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {selectedBookForModal.global_book.BookOpenLevel === 'public' ? (
                    <PublicIcon sx={{ fontSize: 15, color: TOKENS.textSub }} />
                  ) : (
                    <LockIcon sx={{ fontSize: 15, color: TOKENS.textSub }} />
                  )}
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                    {BOOK_OPEN_LEVEL_LABELS[selectedBookForModal.global_book.BookOpenLevel] ?? selectedBookForModal.global_book.BookOpenLevel}
                  </Typography>
                </Box>
                {formatRegisteredAt(selectedBookForModal.global_book.RegisteredDate) && (
                  <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                    登録日時: {formatRegisteredAt(selectedBookForModal.global_book.RegisteredDate)}
                  </Typography>
                )}
              </Box>

              <Tooltip title="本棚の本として共有します">
                <IconButton
                  onClick={() => handleShareBook(selectedBookForModal)}
                  aria-label="share"
                  sx={{
                    flexShrink: 0,
                    color: TOKENS.text,
                    background: TOKENS.buttonBg,
                    border: `1px solid ${TOKENS.borderSoft}`,
                    borderRadius: 999,
                    width: 36,
                    height: 36,
                    '&:hover': { background: TOKENS.buttonBgHover },
                  }}
                >
                  <IosShareIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogContent>

          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              size="large"
              onClick={() => handleViewBookDetail(selectedBookForModal)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                py: 1.5,
                borderRadius: '999px',
                color: TOKENS.bg,
                background: TOKENS.text,
                '&:hover': { background: TOKENS.textSub },
              }}
            >
              本の詳細を見る
            </Button>
            {/* モバイル版mybook_detail_screen.dartと同じく、小さく左寄せの目立たない
                位置にする（誤タップ防止。2026-09-01、大きすぎるとの指摘で修正） */}
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleWithdrawBook(selectedBookForModal)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 12,
                  px: 1.5,
                  py: 0.25,
                  minHeight: 'unset',
                  borderRadius: '999px',
                  color: '#d32f2f',
                  borderColor: '#d32f2f',
                  '&:hover': { borderColor: '#d32f2f', background: 'rgba(211, 47, 47, 0.08)' },
                }}
              >
                除籍する
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Dialog>

    <Dialog
      open={reorderDialogOpen}
      onClose={() => {
        if (reorderSaving) return;
        stopReorderAutoScroll();
        setReorderDialogOpen(false);
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: TOKENS.bg,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>
          本棚内カスタム並び替え
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 0.25 }}>
          {selectedShelfName}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub, mt: 1 }}>
          行をドラッグして順番を入れ替えられます
        </Typography>

        {reorderLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} sx={{ color: TOKENS.text }} />
          </Box>
        ) : (
          <>
            {isLoadingMore && (
              <Typography sx={{ fontSize: 12, color: TOKENS.text, fontWeight: 600, mt: 1.5, textAlign: 'center' }}>
                本棚の読み込み中です。読み込み後に編集できます
              </Typography>
            )}
            <Box sx={{ position: 'relative', mt: 2 }}>
              {isLoadingMore && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    background: 'rgba(0,0,0,0.45)',
                    borderRadius: '12px',
                  }}
                />
              )}
              <Box
                ref={reorderListRef}
                onDragOver={handleReorderListDragOver}
                sx={{
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: '12px',
                }}
              >
                {reorderWorkingBooks.map((book, index) => (
                  <Box
                    key={book.global_book.GlobalBookID}
                    draggable
                    onDragStart={() => setDraggedIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleReorderDrop(index)}
                    onDragEnd={() => {
                      setDraggedIndex(null);
                      stopReorderAutoScroll();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderBottom: `1px solid ${TOKENS.borderSoft}`,
                      background: draggedIndex === index ? TOKENS.glassStrong : TOKENS.buttonBg,
                      opacity: draggedIndex === index ? 0.5 : 1,
                      cursor: 'grab',
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <DragIndicatorIcon sx={{ color: TOKENS.textSub, fontSize: 18, flexShrink: 0 }} />
                    <Box
                      component="img"
                      src={generateCoverUrlSmart(book.global_book.rl_book_id, book.book_details.MainTitle, book.book_details.ISBN)}
                      alt={book.book_details.MainTitle}
                      sx={{ width: 26, height: 38, objectFit: 'contain', flexShrink: 0, background: TOKENS.cardBg, borderRadius: '3px' }}
                    />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.text }} noWrap title={book.book_details.MainTitle}>
                        {book.book_details.MainTitle}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSub }} noWrap title={book.book_details.Responsibility}>
                        {book.book_details.Responsibility}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => moveReorderItem(index, -1)}
                        sx={{ color: TOKENS.textSub, p: 0.25 }}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === reorderWorkingBooks.length - 1}
                        onClick={() => moveReorderItem(index, 1)}
                        sx={{ color: TOKENS.textSub, p: 0.25 }}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
        <Button
          disabled={reorderSaving}
          onClick={() => {
            stopReorderAutoScroll();
            setReorderDialogOpen(false);
          }}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            px: 3,
            color: TOKENS.text,
            background: TOKENS.buttonBg,
            border: `1px solid ${TOKENS.borderSoft}`,
            '&:hover': { background: TOKENS.buttonBgHover },
          }}
        >
          キャンセル
        </Button>
        <Button
          fullWidth
          disabled={reorderLoading || reorderSaving || isLoadingMore || reorderWorkingBooks.length === 0}
          onClick={handleSaveReorder}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '999px',
            color: TOKENS.bg,
            background: TOKENS.text,
            '&:hover': { background: TOKENS.textSub },
          }}
        >
          {reorderSaving ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : '並び順を保存'}
        </Button>
      </Box>
    </Dialog>
    </Box>
  );
};

export default UsersBookshelf;
